import React from "react";
import { Link } from "react-router-dom";

type Variant = "primary" | "secondary" | "ghost" | "link" | "danger" | "accent";
type Size = "sm" | "md" | "lg";

interface CommonProps {
  variant?: Variant;
  size?: Size;
  fullWidth?: boolean;
  loading?: boolean;
  leadingIcon?: React.ReactNode;
  trailingIcon?: React.ReactNode;
  /**
   * Set when the button sits on a `tone="ink"` Section (coffee-900 background
   * in light mode, cream-50 in dark mode). Inverts variant colors so the
   * button is visible against the inverted surface.
   */
  onInk?: boolean;
  className?: string;
  children?: React.ReactNode;
}

type ButtonAsButton = CommonProps &
  Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, keyof CommonProps> & {
    href?: undefined;
    to?: undefined;
  };

type ButtonAsAnchor = CommonProps &
  Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, keyof CommonProps> & {
    href: string;
    to?: undefined;
  };

type ButtonAsLink = CommonProps & {
  to: string;
  href?: undefined;
  type?: undefined;
  onClick?: React.MouseEventHandler;
  target?: string;
  rel?: string;
  "aria-label"?: string;
};

export type ButtonProps = ButtonAsButton | ButtonAsAnchor | ButtonAsLink;

// Editorial-tier button system. Primary = coffee-ink-on-cream (the Ramp move).
// Accent reserved for the rare brand-mark moment. No shadows. No gradients.
const VARIANT: Record<Variant, string> = {
  primary:
    "bg-coffee-900 text-cream-50 hover:bg-coffee-800 active:bg-coffee-900 " +
    "dark:bg-cream-50 dark:text-coffee-900 dark:hover:bg-cream-100 " +
    "focus-visible:ring-coffee-700 dark:focus-visible:ring-cream-200",
  secondary:
    "bg-transparent text-coffee-900 border border-coffee-900 hover:bg-coffee-900 hover:text-cream-50 " +
    "dark:text-cream-50 dark:border-cream-50 dark:hover:bg-cream-50 dark:hover:text-coffee-900 " +
    "focus-visible:ring-coffee-700 dark:focus-visible:ring-cream-200",
  ghost:
    "bg-transparent text-coffee-900 hover:bg-cream-100 " +
    "dark:text-cream-50 dark:hover:bg-coffee-800 " +
    "focus-visible:ring-coffee-400",
  link:
    "text-coffee-900 underline underline-offset-4 decoration-latte-400 hover:decoration-coffee-900 " +
    "dark:text-cream-50 dark:decoration-latte-700 dark:hover:decoration-cream-50 " +
    "focus-visible:ring-coffee-400 px-1",
  danger:
    "bg-danger-500 text-cream-50 hover:bg-danger-700 focus-visible:ring-danger-500",
  accent:
    "bg-accent-500 text-cream-50 hover:bg-accent-700 focus-visible:ring-accent-500",
};

// Inverted variants for use on `tone="ink"` Sections (coffee-900 light /
// cream-50 dark). Each variant is the polarity-flipped twin of VARIANT so
// the button stays visible on the inverted surface.
const VARIANT_INK: Record<Variant, string> = {
  primary:
    "bg-cream-50 text-coffee-900 hover:bg-cream-100 active:bg-cream-50 " +
    "dark:bg-coffee-900 dark:text-cream-50 dark:hover:bg-coffee-800 " +
    "focus-visible:ring-cream-200 dark:focus-visible:ring-coffee-700",
  secondary:
    "bg-transparent text-cream-50 border border-cream-50 hover:bg-cream-50 hover:text-coffee-900 " +
    "dark:text-coffee-900 dark:border-coffee-900 dark:hover:bg-coffee-900 dark:hover:text-cream-50 " +
    "focus-visible:ring-cream-200 dark:focus-visible:ring-coffee-700",
  ghost:
    "bg-transparent text-cream-50 hover:bg-coffee-800 " +
    "dark:text-coffee-900 dark:hover:bg-cream-100 " +
    "focus-visible:ring-cream-200 dark:focus-visible:ring-coffee-400",
  link:
    "text-cream-50 underline underline-offset-4 decoration-latte-700 hover:decoration-cream-50 " +
    "dark:text-coffee-900 dark:decoration-latte-400 dark:hover:decoration-coffee-900 " +
    "focus-visible:ring-cream-200 dark:focus-visible:ring-coffee-400 px-1",
  // Status colors stay the same on either surface — they carry their own
  // contrast against any background.
  danger: "bg-danger-500 text-cream-50 hover:bg-danger-700 focus-visible:ring-danger-500",
  accent: "bg-accent-500 text-cream-50 hover:bg-accent-700 focus-visible:ring-accent-500",
};

const SIZE: Record<Size, string> = {
  sm: "h-9 px-4 text-sm gap-1.5",
  md: "h-11 px-6 text-sm gap-2",
  lg: "h-14 px-8 text-base gap-2.5",
};

const BASE =
  "inline-flex items-center justify-center font-medium rounded-DEFAULT transition-colors duration-150 " +
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 " +
  "disabled:opacity-40 disabled:pointer-events-none whitespace-nowrap";

// Focus-ring offset depends on the surface the button sits on — ink-section
// buttons need the offset to match coffee-900 in light mode and cream-50 in
// dark mode (the inverse of the page-tone case).
const RING_OFFSET = {
  default: "focus-visible:ring-offset-cream-50 dark:focus-visible:ring-offset-coffee-900",
  ink:     "focus-visible:ring-offset-coffee-900 dark:focus-visible:ring-offset-cream-50",
};

function Spinner({ size }: { size: Size }) {
  const dim = size === "sm" ? "h-3.5 w-3.5" : size === "md" ? "h-4 w-4" : "h-5 w-5";
  return (
    <span
      className={`${dim} animate-spin rounded-full border-2 border-current border-t-transparent`}
      aria-hidden="true"
    />
  );
}

export const Button = React.forwardRef<HTMLElement, ButtonProps>(function Button(
  props,
  ref
) {
  const {
    variant = "primary",
    size = "md",
    fullWidth,
    loading,
    leadingIcon,
    trailingIcon,
    onInk,
    className = "",
    children,
    ...rest
  } = props as CommonProps & Record<string, unknown>;

  const sizeClass = variant === "link" ? "" : SIZE[size];
  const widthClass = fullWidth ? "w-full" : "";
  const variantClass = onInk ? VARIANT_INK[variant] : VARIANT[variant];
  const ringOffset = RING_OFFSET[onInk ? "ink" : "default"];

  const cls = [BASE, ringOffset, variantClass, sizeClass, widthClass, className]
    .filter(Boolean)
    .join(" ");

  const inner = loading ? (
    <>
      <Spinner size={size} />
      <span>{children}</span>
    </>
  ) : (
    <>
      {leadingIcon ? <span className="-ml-0.5 inline-flex">{leadingIcon}</span> : null}
      <span>{children}</span>
      {trailingIcon ? <span className="-mr-0.5 inline-flex">{trailingIcon}</span> : null}
    </>
  );

  if ("to" in props && props.to !== undefined) {
    const { to: _to, ...linkRest } = rest as { to: string } & Record<string, unknown>;
    return (
      <Link
        ref={ref as React.Ref<HTMLAnchorElement>}
        to={props.to}
        className={cls}
        {...(linkRest as object)}
      >
        {inner}
      </Link>
    );
  }

  if ("href" in props && props.href !== undefined) {
    return (
      <a
        ref={ref as React.Ref<HTMLAnchorElement>}
        className={cls}
        {...(rest as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
        href={props.href as string}
      >
        {inner}
      </a>
    );
  }

  return (
    <button
      ref={ref as React.Ref<HTMLButtonElement>}
      type={(rest as { type?: "button" | "submit" | "reset" }).type ?? "button"}
      disabled={(rest as { disabled?: boolean }).disabled || loading}
      className={cls}
      {...(rest as React.ButtonHTMLAttributes<HTMLButtonElement>)}
    >
      {inner}
    </button>
  );
});

export default Button;
